import React, { useEffect, useState } from 'react';
import { PublicClientApplication, LogLevel } from '@azure/msal-browser';

/**
 * This page runs inside the Teams auth dialog (opened by teams.authentication.authenticate()).
 * It handles MSAL redirect flow and passes the token back to the parent frame via notifySuccess.
 *
 * Flow:
 *  1. Teams opens /auth-popup in a dialog
 *  2. This page initializes MSAL, checks for redirect response
 *  3. If no redirect response → starts loginRedirect to Azure AD
 *  4. Azure redirects back to /auth-popup with auth code
 *  5. MSAL exchanges code for tokens, handleRedirectPromise resolves
 *  6. Calls teams.authentication.notifySuccess(tokenPayload)
 */

const clientId = process.env.REACT_APP_AZURE_CLIENT_ID || '';
const tenantId = process.env.REACT_APP_AZURE_TENANT_ID || 'common';

// Dedicated MSAL instance for this popup — redirects back to /auth-popup
const popupMsalConfig = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: `${window.location.origin}/auth-popup`,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true, // Needed for IE/Edge in Teams desktop
  },
  system: {
    loggerOptions: { logLevel: LogLevel.Warning },
  },
};

const popupMsal = new PublicClientApplication(popupMsalConfig);
const loginScopes = { scopes: ['User.Read', 'openid', 'profile', 'email'] };

export default function TeamsAuthPopup() {
  const [status, setStatus] = useState('Signing in with Microsoft...');

  useEffect(() => {
    let cancelled = false;

    async function handleAuth() {
      try {
        const teams = await import('@microsoft/teams-js');
        await teams.app.initialize();
        await popupMsal.initialize();

        // Check if this is a redirect return (second load)
        const response = await popupMsal.handleRedirectPromise();

        if (response && response.accessToken) {
          // Got tokens — send back to parent frame
          const payload = JSON.stringify({
            accessToken: response.accessToken,
            idToken: response.idToken,
            account: {
              name: response.account?.name || '',
              username: response.account?.username || '',
            },
          });
          teams.authentication.notifySuccess(payload);
          return;
        }

        // No redirect response — first load, start the login redirect
        if (!cancelled) {
          setStatus('Redirecting to Microsoft login...');
          await popupMsal.loginRedirect(loginScopes);
        }
      } catch (err) {
        console.error('Teams auth popup error:', err);
        if (!cancelled) {
          setStatus(`Authentication failed: ${err.message || 'Unknown error'}`);
        }
        try {
          const teams = await import('@microsoft/teams-js');
          teams.authentication.notifyFailure(err.message || 'Authentication failed');
        } catch {
          // Can't notify Teams
        }
      }
    }

    handleAuth();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'Segoe UI, system-ui, sans-serif',
      backgroundColor: '#fafafa',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 32, height: 32,
          border: '3px solid #e5e5e5',
          borderTopColor: '#0078d4',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px',
        }} />
        <p style={{ color: '#666', fontSize: 14 }}>{status}</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
